import { createSelector } from '@reduxjs/toolkit';

import { selectUsersById } from '@/slices/account/account.selector';
import type { User } from '@/slices/account/account.types';
import consultingCompanyApi from '@/slices/consultingCompany/consultingCompany.api';
import type {
  ConsultingCompany,
  HydratedConsultingCompany,
} from '@/slices/consultingCompany/consultingCompany.types';
import { selectHydratedEnterprisesById } from '@/slices/enterprises/enterprise.selector';
import type { HydratedEnterprise } from '@/slices/enterprises/enterprise.types';
import type { RootState } from '@/store';

const selectConsultingCompanies = (state: RootState): ConsultingCompany[] =>
  consultingCompanyApi.endpoints.getConsultingCompanies.select()(state).data ??
  [];

export const selectHydratedConsultingCompanies = createSelector(
  [selectConsultingCompanies, selectUsersById, selectHydratedEnterprisesById],
  (
    consultingCompanies,
    usersById,
    hydratedEnterprisesById,
  ): HydratedConsultingCompany[] =>
    consultingCompanies.map((company) => ({
      ...company,
      createdByResolved: company.createdBy
        ? (usersById[company.createdBy] ?? null)
        : null,
      employeesResolved: (company.employees ?? [])
        .map((employeeId) => usersById[employeeId])
        .filter((employee): employee is User => Boolean(employee)),
      projectsResolved: (company.projects ?? [])
        .map((enterpriseId) => hydratedEnterprisesById[enterpriseId])
        .filter((enterprise): enterprise is HydratedEnterprise =>
          Boolean(enterprise),
        ),
    })),
);
